import { useState, useEffect, useRef } from 'react';
import type { NavigateFunction } from 'react-router-dom';
import { WorkoutService, parseDuplicateWorkoutError } from '../../../../lib/workoutService';
import { ExerciseService } from '../../../../lib/exerciseService';
import type { TrainerExerciseListRow } from '../../../../types/dataModel';
import { isTrainerOwnedLibraryRow, normalizeWorkoutSummaryRow } from '../utils/workoutLibraryRowUtils';
import type { WorkoutSortColumn } from '../types/library';
import type { useLibraryExercises } from './useLibraryExercises';
import type { useLibraryWorkoutPlans } from './useLibraryWorkoutPlans';
import type { useLibraryWorkouts } from './useLibraryWorkouts';

export type UseLibraryMutationsParams = {
  profileId: string | undefined;
  workouts: any[];
  setWorkouts: React.Dispatch<React.SetStateAction<any[]>>;
  workoutPlans: any[];
  setWorkoutPlans: React.Dispatch<React.SetStateAction<any[]>>;
  exercises: TrainerExerciseListRow[];
  setExercises: React.Dispatch<React.SetStateAction<TrainerExerciseListRow[]>>;
  loadLibraryContent: (options?: {
    silent?: boolean;
    targets?: Array<'workouts' | 'workoutPlans' | 'exercises'>;
  }) => Promise<void>;
  loadAllExercisesForLibrary: (options?: { silent?: boolean }) => Promise<void>;
  navigate: NavigateFunction;
  wk: ReturnType<typeof useLibraryWorkouts>;
  pl: ReturnType<typeof useLibraryWorkoutPlans>;
  ex: ReturnType<typeof useLibraryExercises>;
};

/** Library row/bulk mutations, selection, menus (P15-R5). */
export function useLibraryMutations({
  profileId,
  workouts,
  setWorkouts,
  workoutPlans,
  setWorkoutPlans,
  exercises,
  setExercises,
  loadLibraryContent,
  loadAllExercisesForLibrary,
  navigate,
  wk,
  pl,
  ex,
}: UseLibraryMutationsParams) {
  const [workoutDeleteConfirm, setWorkoutDeleteConfirm] = useState<{ id: string; name: string } | null>(null);
  const [workoutPlanDeleteConfirm, setWorkoutPlanDeleteConfirm] = useState<{
    id: string;
    name: string;
  } | null>(null);
  const [exerciseDeleteConfirm, setExerciseDeleteConfirm] = useState<{ id: string; name: string } | null>(
    null,
  );
  const [workoutsBulkDeleteState, setWorkoutsBulkDeleteState] = useState<{
    ids: string[];
    deleting: boolean;
    skippedCatalogCount?: number;
  } | null>(null);
  const [workoutPlansBulkDeleteState, setWorkoutPlansBulkDeleteState] = useState<{
    ids: string[];
    deleting: boolean;
    skippedCatalogCount?: number;
  } | null>(null);
  const [bulkDeleteState, setBulkDeleteState] = useState<{
    ownedIds: string[];
    systemCount: number;
    deleting: boolean;
  } | null>(null);
  const [bulkDeleteToast, setBulkDeleteToast] = useState<string | null>(null);
  const [bulkDuplicateExercisesInFlight, setBulkDuplicateExercisesInFlight] = useState(false);
  const [trainerWorkoutDuplicateInFlight, setTrainerWorkoutDuplicateInFlight] = useState(false);
  const [trainerPlanDuplicateInFlight, setTrainerPlanDuplicateInFlight] = useState(false);

  const [openExerciseMenu, setOpenExerciseMenu] = useState<string | null>(null);
  const exerciseMenuRef = useRef<HTMLDivElement>(null);
  const [openWorkoutMenu, setOpenWorkoutMenu] = useState<string | null>(null);
  const workoutMenuRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});
  const [selectedWorkouts, setSelectedWorkouts] = useState<Set<string>>(new Set());
  const [selectedWorkoutPlans, setSelectedWorkoutPlans] = useState<Set<string>>(new Set());
  const [isActionsMenuOpen, setIsActionsMenuOpen] = useState(false);
  const [isWorkoutPlanActionsMenuOpen, setIsWorkoutPlanActionsMenuOpen] = useState(false);
  const actionsMenuRef = useRef<HTMLDivElement | null>(null);
  const workoutPlanActionsMenuRef = useRef<HTMLDivElement | null>(null);
  const [openWorkoutPlanMenu, setOpenWorkoutPlanMenu] = useState<string | null>(null);
  const workoutPlanMenuRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});

  const [openWorkoutFilterColumn, setOpenWorkoutFilterColumn] = useState<WorkoutSortColumn | null>(null);
  const workoutFilterRefs = {
    name: useRef<HTMLDivElement>(null),
    createdAt: useRef<HTMLDivElement>(null),
    exercises: useRef<HTMLDivElement>(null),
  };
  const [selectedExercises, setSelectedExercises] = useState<Set<string>>(new Set());

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent | TouchEvent) => {
      if (openWorkoutMenu) {
        const menuRef = workoutMenuRefs.current[openWorkoutMenu];
        if (menuRef && !menuRef.contains(event.target as Node)) {
          setOpenWorkoutMenu(null);
        }
      }
      if (openWorkoutFilterColumn) {
        const filterRef = workoutFilterRefs[openWorkoutFilterColumn].current;
        if (filterRef && !filterRef.contains(event.target as Node)) {
          setOpenWorkoutFilterColumn(null);
        }
      }
      if (isActionsMenuOpen) {
        if (actionsMenuRef.current && !actionsMenuRef.current.contains(event.target as Node)) {
          setIsActionsMenuOpen(false);
        }
      }
      if (isWorkoutPlanActionsMenuOpen) {
        if (
          workoutPlanActionsMenuRef.current &&
          !workoutPlanActionsMenuRef.current.contains(event.target as Node)
        ) {
          setIsWorkoutPlanActionsMenuOpen(false);
        }
      }
      if (openWorkoutPlanMenu) {
        const clickedOutside = Object.values(workoutPlanMenuRefs.current).every(
          (ref) => ref && !ref.contains(event.target as Node),
        );
        if (clickedOutside) {
          setOpenWorkoutPlanMenu(null);
        }
      }
      if (openExerciseMenu && exerciseMenuRef.current && !exerciseMenuRef.current.contains(event.target as Node)) {
        setOpenExerciseMenu(null);
      }
    };

    if (
      openWorkoutMenu ||
      openWorkoutFilterColumn ||
      isActionsMenuOpen ||
      isWorkoutPlanActionsMenuOpen ||
      openWorkoutPlanMenu ||
      openExerciseMenu
    ) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('touchstart', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [
    openWorkoutMenu,
    openWorkoutFilterColumn,
    isActionsMenuOpen,
    isWorkoutPlanActionsMenuOpen,
    openWorkoutPlanMenu,
    openExerciseMenu,
  ]);

  const handleDeleteExercise = (exerciseId: string, exerciseName: string) => {
    setExerciseDeleteConfirm({ id: exerciseId, name: exerciseName });
    setOpenExerciseMenu(null);
  };

  const confirmDeleteExercise = async (exerciseId: string) => {
    try {
      await ExerciseService.deleteExercise(exerciseId);
      setExerciseDeleteConfirm(null);
      setSelectedExercises((prev) => {
        if (!prev.has(exerciseId)) return prev;
        const next = new Set(prev);
        next.delete(exerciseId);
        return next;
      });
      setExercises((prev) => prev.filter((row) => row.id !== exerciseId));
      void loadAllExercisesForLibrary({ silent: true });
    } catch (error) {
      console.error('Error deleting exercise:', error);
      alert(error instanceof Error ? error.message : 'Failed to delete exercise.');
    }
  };

  const handleRequestBulkDeleteExercises = () => {
    if (selectedExercises.size === 0) return;
    const ownedIds: string[] = [];
    let systemCount = 0;
    for (const row of exercises) {
      if (!selectedExercises.has(row.id)) continue;
      if (row.createdBy === profileId) ownedIds.push(row.id);
      else systemCount++;
    }
    if (ownedIds.length === 0) {
      setBulkDeleteToast('None of the selected exercises are yours to delete.');
      window.setTimeout(() => setBulkDeleteToast(null), 3500);
      return;
    }
    setBulkDeleteState({ ownedIds, systemCount, deleting: false });
  };

  const handleConfirmBulkDeleteExercises = async () => {
    if (!bulkDeleteState || bulkDeleteState.deleting) return;
    setBulkDeleteState({ ...bulkDeleteState, deleting: true });
    try {
      await ExerciseService.deleteExercises(bulkDeleteState.ownedIds);
      const deleted = new Set(bulkDeleteState.ownedIds);
      setExercises((prev) => prev.filter((row) => !deleted.has(row.id)));
      setSelectedExercises(new Set());
      setBulkDeleteState(null);
      void loadAllExercisesForLibrary({ silent: true });
    } catch (error) {
      console.error('Error bulk-deleting exercises:', error);
      alert(error instanceof Error ? error.message : 'Failed to delete exercises.');
      setBulkDeleteState((prev) => (prev ? { ...prev, deleting: false } : prev));
    }
  };

  const handleBulkDuplicateExercises = async () => {
    if (selectedExercises.size === 0 || bulkDuplicateExercisesInFlight) return;
    if (!profileId) {
      alert('You must be signed in to duplicate exercises.');
      return;
    }
    const ids = Array.from(selectedExercises);
    const existingTrainerNames = new Set<string>();
    for (const row of exercises) {
      if (row.createdBy === profileId) existingTrainerNames.add(row.name);
    }
    setBulkDuplicateExercisesInFlight(true);
    try {
      await ExerciseService.duplicateExercises(ids, profileId, existingTrainerNames);
      setSelectedExercises(new Set());
      await loadAllExercisesForLibrary({ silent: true });
    } catch (error) {
      console.error('Error duplicating exercises:', error);
      alert(error instanceof Error ? error.message : 'Failed to duplicate some exercises. Please try again.');
    } finally {
      setBulkDuplicateExercisesInFlight(false);
    }
  };

  const handleDuplicateExerciseRow = async (id: string) => {
    if (!profileId) {
      alert('You must be signed in to duplicate exercises.');
      return;
    }
    const existingTrainerNames = new Set<string>();
    for (const row of exercises) {
      if (row.createdBy === profileId) existingTrainerNames.add(row.name);
    }
    try {
      await ExerciseService.duplicateExercise(id, profileId, existingTrainerNames);
      await loadAllExercisesForLibrary({ silent: true });
      ex.setLibExercisesPage(1);
    } catch (error) {
      console.error('Error duplicating exercise:', error);
      alert(error instanceof Error ? error.message : 'Failed to duplicate exercise. Please try again.');
    }
  };

  const handleDuplicateWorkout = async (workout: any) => {
    if (!workout?.id) return;
    if (!profileId) {
      alert('You must be signed in to duplicate workouts.');
      return;
    }
    if (trainerWorkoutDuplicateInFlight) return;
    setTrainerWorkoutDuplicateInFlight(true);
    try {
      const dup = await WorkoutService.duplicateTrainerWorkout(workout.id);
      const optimisticRow: any = normalizeWorkoutSummaryRow({
        id: dup.new_workout_id,
        name: dup.new_workout_name,
        note: workout.note ?? null,
        estimatedDurationMinutes: workout.estimatedDurationMinutes,
        difficulty: workout.difficulty,
        createdBy: profileId,
        isPublic: false,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        exerciseCount: dup.distinct_exercise_count,
        focusTags: workout.focusTags ?? [],
        equipmentTags: workout.equipmentTags ?? [],
        bannerImageUrl: null,
      });
      setWorkouts((prev) => [optimisticRow, ...prev]);
      wk.setLibWorkoutsPage(1);
      setOpenWorkoutMenu(null);
      await loadLibraryContent({ silent: true, targets: ['workouts'] });
    } catch (error) {
      console.error('Error duplicating workout:', error);
      alert(parseDuplicateWorkoutError(error));
    } finally {
      setTrainerWorkoutDuplicateInFlight(false);
    }
  };

  const handleDeleteWorkout = (workoutId: string, workoutName: string) => {
    const row = workouts.find((w: { id: string; createdBy?: string | null }) => w.id === workoutId);
    if (row && !isTrainerOwnedLibraryRow(row.createdBy, profileId)) {
      alert('Bravos catalog workouts cannot be deleted from your library.');
      return;
    }
    setWorkoutDeleteConfirm({ id: workoutId, name: workoutName });
    setOpenWorkoutMenu(null);
  };

  const confirmDeleteWorkout = async (workoutId: string) => {
    try {
      await WorkoutService.deleteWorkout(workoutId);
      setWorkoutDeleteConfirm(null);
      setSelectedWorkouts((prev) => {
        const newSet = new Set(prev);
        newSet.delete(workoutId);
        return newSet;
      });
      setWorkouts((prev) => prev.filter((w: any) => w.id !== workoutId));
      await loadLibraryContent();
    } catch (error) {
      console.error('Error deleting workout:', error);
      alert('Failed to delete workout. Please try again.');
      await loadLibraryContent();
    }
  };

  const handleSelectWorkout = (workoutId: string) => {
    setSelectedWorkouts((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(workoutId)) newSet.delete(workoutId);
      else newSet.add(workoutId);
      return newSet;
    });
  };

  const handleSelectAllWorkouts = () => {
    if (selectedWorkouts.size === wk.filteredAndSortedWorkouts.length) {
      setSelectedWorkouts(new Set());
    } else {
      setSelectedWorkouts(new Set(wk.filteredAndSortedWorkouts.map((w) => w.id)));
    }
  };

  const handleDeleteSelectedWorkouts = () => {
    if (selectedWorkouts.size === 0) return;
    const ownedIds: string[] = [];
    let catalogCount = 0;
    for (const row of workouts) {
      if (!selectedWorkouts.has(row.id)) continue;
      if (isTrainerOwnedLibraryRow(row.createdBy, profileId)) ownedIds.push(row.id);
      else catalogCount++;
    }
    if (ownedIds.length === 0) {
      alert('None of the selected workouts are yours to delete.');
      return;
    }
    setWorkoutsBulkDeleteState({
      ids: ownedIds,
      deleting: false,
      skippedCatalogCount: catalogCount > 0 ? catalogCount : undefined,
    });
    setIsActionsMenuOpen(false);
  };

  const confirmBulkDeleteWorkouts = async () => {
    if (!workoutsBulkDeleteState || workoutsBulkDeleteState.deleting) return;
    setWorkoutsBulkDeleteState({ ...workoutsBulkDeleteState, deleting: true });
    const removedIds = workoutsBulkDeleteState.ids;
    try {
      await Promise.all(removedIds.map((workoutId) => WorkoutService.deleteWorkout(workoutId)));
      setWorkoutsBulkDeleteState(null);
      setSelectedWorkouts(new Set());
      setOpenWorkoutMenu(null);
      setWorkouts((prev) => prev.filter((w: any) => !removedIds.includes(w.id)));
      await loadLibraryContent();
    } catch (error) {
      console.error('Error deleting workouts:', error);
      alert('Failed to delete some workouts. Please try again.');
      setWorkoutsBulkDeleteState((prev) => (prev ? { ...prev, deleting: false } : prev));
    }
  };

  const handleDuplicateSelectedWorkouts = async () => {
    if (selectedWorkouts.size === 0 || trainerWorkoutDuplicateInFlight) return;
    if (!profileId) {
      alert('You must be signed in to duplicate workouts.');
      return;
    }
    const workoutsToDuplicate = workouts.filter((w) => selectedWorkouts.has(w.id));
    setTrainerWorkoutDuplicateInFlight(true);
    const failures: string[] = [];
    try {
      for (const w of workoutsToDuplicate) {
        try {
          await WorkoutService.duplicateTrainerWorkout(w.id);
        } catch (e) {
          failures.push(parseDuplicateWorkoutError(e));
        }
      }
      setSelectedWorkouts(new Set());
      setIsActionsMenuOpen(false);
      await loadLibraryContent({ silent: true, targets: ['workouts'] });
      const msgs = Array.from(new Set(failures));
      if (msgs.length > 0) alert(msgs.join('\n\n'));
    } finally {
      setTrainerWorkoutDuplicateInFlight(false);
    }
  };

  const filteredWorkoutPlans = pl.filteredWorkoutPlansByName;

  const handleSelectWorkoutPlan = (planId: string) => {
    setSelectedWorkoutPlans((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(planId)) newSet.delete(planId);
      else newSet.add(planId);
      return newSet;
    });
  };

  const handleSelectAllWorkoutPlans = () => {
    if (selectedWorkoutPlans.size === filteredWorkoutPlans.length) {
      setSelectedWorkoutPlans(new Set());
    } else {
      setSelectedWorkoutPlans(new Set(filteredWorkoutPlans.map((p) => p.id)));
    }
  };

  const handleDeleteSelectedWorkoutPlans = () => {
    if (selectedWorkoutPlans.size === 0) return;
    const ownedIds: string[] = [];
    let catalogCount = 0;
    for (const row of workoutPlans) {
      if (!selectedWorkoutPlans.has(row.id)) continue;
      if (isTrainerOwnedLibraryRow(row.createdBy, profileId)) ownedIds.push(row.id);
      else catalogCount++;
    }
    if (ownedIds.length === 0) {
      alert('None of the selected workout plans are yours to delete.');
      return;
    }
    setWorkoutPlansBulkDeleteState({
      ids: ownedIds,
      deleting: false,
      skippedCatalogCount: catalogCount > 0 ? catalogCount : undefined,
    });
    setIsWorkoutPlanActionsMenuOpen(false);
  };

  const confirmBulkDeleteWorkoutPlans = async () => {
    if (!workoutPlansBulkDeleteState || workoutPlansBulkDeleteState.deleting) return;
    setWorkoutPlansBulkDeleteState({ ...workoutPlansBulkDeleteState, deleting: true });
    const removedIds = workoutPlansBulkDeleteState.ids;
    try {
      await Promise.all(removedIds.map((planId) => WorkoutService.deleteWorkoutPlan(planId)));
      setWorkoutPlansBulkDeleteState(null);
      setSelectedWorkoutPlans(new Set());
      await loadLibraryContent();
    } catch (error) {
      console.error('Error deleting workout plans:', error);
      alert('Failed to delete some workout plans. Please try again.');
      setWorkoutPlansBulkDeleteState((prev) => (prev ? { ...prev, deleting: false } : prev));
    }
  };

  const handleDuplicateSelectedWorkoutPlans = async () => {
    if (selectedWorkoutPlans.size === 0 || trainerPlanDuplicateInFlight) return;
    if (!profileId) {
      alert('You must be signed in to duplicate workout plans.');
      return;
    }
    const plansToDuplicate = workoutPlans.filter((p) => selectedWorkoutPlans.has(p.id));
    setTrainerPlanDuplicateInFlight(true);
    const failures: string[] = [];
    try {
      for (const p of plansToDuplicate) {
        try {
          await WorkoutService.duplicateTrainerWorkoutPlan(p.id);
        } catch (e) {
          failures.push(
            e instanceof Error ? e.message : 'Failed to duplicate a workout plan. Please try again.',
          );
        }
      }
      setSelectedWorkoutPlans(new Set());
      setIsWorkoutPlanActionsMenuOpen(false);
      await loadLibraryContent({ silent: true, targets: ['workoutPlans'] });
      const msgs = Array.from(new Set(failures));
      if (msgs.length > 0) alert(msgs.join('\n\n'));
    } finally {
      setTrainerPlanDuplicateInFlight(false);
    }
  };

  const handleDuplicateWorkoutPlan = async (planId: string) => {
    const planRow = workoutPlans.find((p) => p.id === planId);
    if (!profileId) {
      alert('You must be signed in to duplicate workout plans.');
      return;
    }
    if (trainerPlanDuplicateInFlight) return;
    setTrainerPlanDuplicateInFlight(true);
    try {
      const dup = await WorkoutService.duplicateTrainerWorkoutPlan(planId);
      const optimisticRow: any = {
        id: dup.newPlanId,
        name: dup.newPlanName,
        description: planRow?.description ?? '',
        totalNoOfDays: planRow?.totalNoOfDays,
        difficulty: planRow?.difficulty,
        createdBy: profileId,
        createdAt: new Date().toISOString(),
        workoutCount:
          typeof planRow?.workoutCount === 'number' ? planRow.workoutCount : dup.scheduleRowsCopied,
      };
      setWorkoutPlans((prev) => [optimisticRow, ...prev]);
      pl.setLibPlansPage(1);
      setOpenWorkoutPlanMenu(null);
      await loadLibraryContent({ silent: true, targets: ['workoutPlans'] });
    } catch (error) {
      console.error('Error duplicating workout plan:', error);
      alert(
        error instanceof Error ? error.message : 'Failed to duplicate workout plan. Please try again.',
      );
    } finally {
      setTrainerPlanDuplicateInFlight(false);
    }
  };

  const handleDeleteWorkoutPlan = (planId: string, planName: string) => {
    const row = workoutPlans.find((p: { id: string; createdBy?: string | null }) => p.id === planId);
    if (row && !isTrainerOwnedLibraryRow(row.createdBy, profileId)) {
      alert('Bravos catalog workout plans cannot be deleted from your library.');
      return;
    }
    setWorkoutPlanDeleteConfirm({ id: planId, name: planName });
    setOpenWorkoutPlanMenu(null);
  };

  const confirmDeleteWorkoutPlan = async (planId: string) => {
    try {
      await WorkoutService.deleteWorkoutPlan(planId);
      setWorkoutPlanDeleteConfirm(null);
      await loadLibraryContent();
    } catch (error) {
      console.error('Error deleting workout plan:', error);
      alert('Failed to delete workout plan. Please try again.');
    }
  };

  const handleEditWorkoutPlan = (planId: string) => {
    setOpenWorkoutPlanMenu(null);
    navigate(`/trainer-dashboard/workout-plan/${planId}?mode=edit`);
  };

  const libPageSelectionState: 'none' | 'some' | 'all' = (() => {
    if (wk.libWorkoutsPageRows.length === 0) return 'none';
    let selectedCount = 0;
    for (const w of wk.libWorkoutsPageRows) if (selectedWorkouts.has(w.id)) selectedCount++;
    if (selectedCount === 0) return 'none';
    if (selectedCount === wk.libWorkoutsPageRows.length) return 'all';
    return 'some';
  })();

  const handleLibToggleSelectPage = () => {
    setSelectedWorkouts((prev) => {
      const next = new Set(prev);
      if (libPageSelectionState === 'all') {
        for (const w of wk.libWorkoutsPageRows) next.delete(w.id);
      } else {
        for (const w of wk.libWorkoutsPageRows) next.add(w.id);
      }
      return next;
    });
  };

  const libPlansPageSelectionState: 'none' | 'some' | 'all' = (() => {
    if (pl.libPlansPageRows.length === 0) return 'none';
    let selected = 0;
    for (const p of pl.libPlansPageRows) if (selectedWorkoutPlans.has(p.id)) selected++;
    if (selected === 0) return 'none';
    if (selected === pl.libPlansPageRows.length) return 'all';
    return 'some';
  })();

  const handleLibToggleSelectPlansPage = () => {
    setSelectedWorkoutPlans((prev) => {
      const next = new Set(prev);
      if (libPlansPageSelectionState === 'all') {
        for (const p of pl.libPlansPageRows) next.delete(p.id);
      } else {
        for (const p of pl.libPlansPageRows) next.add(p.id);
      }
      return next;
    });
  };

  const libExercisesPageSelectionState: 'none' | 'some' | 'all' = (() => {
    if (ex.libExercisesPageRows.length === 0) return 'none';
    let selectedCount = 0;
    for (const row of ex.libExercisesPageRows) if (selectedExercises.has(row.id)) selectedCount++;
    if (selectedCount === 0) return 'none';
    if (selectedCount === ex.libExercisesPageRows.length) return 'all';
    return 'some';
  })();

  const handleLibToggleSelectExercisesPage = () => {
    setSelectedExercises((prev) => {
      const next = new Set(prev);
      if (libExercisesPageSelectionState === 'all') {
        for (const row of ex.libExercisesPageRows) next.delete(row.id);
      } else {
        for (const row of ex.libExercisesPageRows) next.add(row.id);
      }
      return next;
    });
  };

  const handleLibToggleSelectExercise = (id: string) => {
    setSelectedExercises((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleWorkoutFilter = (column: WorkoutSortColumn, e: React.MouseEvent) => {
    e.stopPropagation();
    setOpenWorkoutFilterColumn(openWorkoutFilterColumn === column ? null : column);
  };

  return {
    workoutDeleteConfirm,
    setWorkoutDeleteConfirm,
    workoutPlanDeleteConfirm,
    setWorkoutPlanDeleteConfirm,
    exerciseDeleteConfirm,
    setExerciseDeleteConfirm,
    workoutsBulkDeleteState,
    setWorkoutsBulkDeleteState,
    workoutPlansBulkDeleteState,
    setWorkoutPlansBulkDeleteState,
    bulkDeleteState,
    setBulkDeleteState,
    bulkDeleteToast,
    setBulkDeleteToast,
    bulkDuplicateExercisesInFlight,
    trainerWorkoutDuplicateInFlight,
    trainerPlanDuplicateInFlight,
    openExerciseMenu,
    setOpenExerciseMenu,
    exerciseMenuRef,
    openWorkoutMenu,
    setOpenWorkoutMenu,
    workoutMenuRefs,
    selectedWorkouts,
    setSelectedWorkouts,
    selectedWorkoutPlans,
    setSelectedWorkoutPlans,
    isActionsMenuOpen,
    setIsActionsMenuOpen,
    isWorkoutPlanActionsMenuOpen,
    setIsWorkoutPlanActionsMenuOpen,
    actionsMenuRef,
    workoutPlanActionsMenuRef,
    openWorkoutPlanMenu,
    setOpenWorkoutPlanMenu,
    workoutPlanMenuRefs,
    openWorkoutFilterColumn,
    setOpenWorkoutFilterColumn,
    workoutFilterRefs,
    selectedExercises,
    setSelectedExercises,
    handleDeleteExercise,
    confirmDeleteExercise,
    handleRequestBulkDeleteExercises,
    handleConfirmBulkDeleteExercises,
    handleBulkDuplicateExercises,
    handleDuplicateExerciseRow,
    handleDuplicateWorkout,
    handleDeleteWorkout,
    confirmDeleteWorkout,
    handleSelectWorkout,
    handleSelectAllWorkouts,
    handleDeleteSelectedWorkouts,
    confirmBulkDeleteWorkouts,
    handleDuplicateSelectedWorkouts,
    handleSelectWorkoutPlan,
    handleSelectAllWorkoutPlans,
    handleDeleteSelectedWorkoutPlans,
    confirmBulkDeleteWorkoutPlans,
    handleDuplicateSelectedWorkoutPlans,
    handleDuplicateWorkoutPlan,
    handleDeleteWorkoutPlan,
    confirmDeleteWorkoutPlan,
    handleEditWorkoutPlan,
    libPageSelectionState,
    handleLibToggleSelectPage,
    libPlansPageSelectionState,
    handleLibToggleSelectPlansPage,
    libExercisesPageSelectionState,
    handleLibToggleSelectExercisesPage,
    handleLibToggleSelectExercise,
    toggleWorkoutFilter,
  };
}
